import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { IconButton } from '@/shared/ui/IconButton/IconButton';
import {
  addDays,
  addMonths,
  clampIso,
  compareIso,
  formatRuLong,
  isBetween,
  isSameMonth,
  isWeekend,
  isoToday,
  monthGrid,
  monthLabel,
  monthNames,
  startOfMonth,
  WEEKDAYS,
  weekdayIndex,
} from './calendar-date';
import styles from './Calendar.module.css';

/**
 * Месячная сетка — сама по себе, без поля ввода.
 *
 * В Base UI календаря нет (проверено на `1.0.0-rc.0`), поэтому сетка своя:
 * от библиотеки здесь только всплывашка у пикеров. Зато и правила сетки —
 * наши: неделя с понедельника, шесть строк всегда, дни соседних месяцев
 * кликабельны.
 *
 * Значение — строка `YYYY-MM-DD`, а не `Date`: почему именно так, разобрано в
 * шапке `calendar-date.ts`.
 *
 * Клавиатура обязательна, а не приятна: дату вводят пачками (сроки задач,
 * исключения календаря), и мышь на каждое значение — это медленно. Стрелки
 * ходят по дням и неделям, `PageUp`/`PageDown` листают месяцы (с `Shift` —
 * годы), `Home`/`End` прыгают на края недели, `Enter` выбирает. Фокус в сетке
 * один (roving tabindex): `Tab` выводит из календаря целиком, а не обходит
 * сорок две ячейки.
 *
 * @use месячная сетка дат сама по себе — без поля ввода: выбор дня на панели, подсветка периода. Поле с датой — это DatePicker.
 */

export type CalendarMode = 'single' | 'range';

export interface CalendarProps {
  mode?: CalendarMode;
  value?: string;
  from?: string;
  to?: string;
  onSelect: (iso: string) => void;
  month?: string;
  onMonthChange?: (month: string) => void;
  defaultMonth?: string;
  min?: string;
  max?: string;
  isDisabled?: (iso: string) => boolean;
  months?: number;
  preview?: string;
  onPreviewChange?: (iso: string) => void;
  footer?: React.ReactNode;
  autoFocusDay?: boolean;
  className?: string;
  'aria-label'?: string;
}

function initialCursor(props: CalendarProps, today: string): string {
  const candidate = props.value || props.from || props.month || props.defaultMonth || today;
  return clampIso(candidate, props.min, props.max);
}

export const Calendar: React.FC<CalendarProps> = (props) => {
  const {
    mode = 'single',
    value = '',
    from = '',
    to = '',
    onSelect,
    month,
    onMonthChange,
    defaultMonth,
    min,
    max,
    isDisabled,
    months = 1,
    preview = '',
    onPreviewChange,
    footer,
    autoFocusDay,
    className,
  } = props;

  const today = React.useMemo(() => isoToday(), []);

  const [ownMonth, setOwnMonth] = React.useState(() =>
    startOfMonth(month || defaultMonth || initialCursor(props, today)),
  );
  const visibleMonth = startOfMonth(month ?? ownMonth);

  const setMonth = React.useCallback(
    (next: string) => {
      const normalized = startOfMonth(next);
      if (month == null) setOwnMonth(normalized);
      onMonthChange?.(normalized);
    },
    [month, onMonthChange],
  );

  const [cursor, setCursor] = React.useState(() => initialCursor(props, today));
  const [picking, setPicking] = React.useState<number | null>(null);
  const gridRef = React.useRef<HTMLDivElement>(null);
  const wantFocus = React.useRef(Boolean(autoFocusDay));

  React.useEffect(() => {
    const selected = mode === 'range' ? to || from : value;
    if (selected === '') return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCursor((current) => (current === selected ? current : selected));
  }, [mode, value, from, to]);

  const lastVisible = addMonths(visibleMonth, months - 1);

  const revealCursor = (iso: string) => {
    const cursorMonth = startOfMonth(iso);
    if (compareIso(cursorMonth, visibleMonth) < 0) setMonth(cursorMonth);
    else if (compareIso(cursorMonth, lastVisible) > 0) {
      setMonth(addMonths(cursorMonth, -(months - 1)));
    }
  };

  React.useLayoutEffect(() => {
    if (!wantFocus.current) return;
    const node = gridRef.current?.querySelector<HTMLElement>(`[data-iso="${cursor}"]`);
    node?.focus();
  }, [cursor, visibleMonth]);

  const dayDisabled = React.useCallback(
    (iso: string) => {
      if (min != null && min !== '' && compareIso(iso, min) < 0) return true;
      if (max != null && max !== '' && compareIso(iso, max) > 0) return true;
      return isDisabled?.(iso) ?? false;
    },
    [min, max, isDisabled],
  );

  const pick = (iso: string) => {
    if (dayDisabled(iso)) return;
    setCursor(iso);
    onSelect(iso);
  };

  const moveCursor = (next: string) => {
    wantFocus.current = true;
    const target = clampIso(next, min, max);
    setCursor(target);
    revealCursor(target);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const step: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
    };
    if (event.key in step) {
      event.preventDefault();
      moveCursor(addDays(cursor, step[event.key]));
      return;
    }
    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      const weekday = weekdayIndex(cursor);
      moveCursor(addDays(cursor, event.key === 'Home' ? -weekday : 6 - weekday));
      return;
    }
    if (event.key === 'PageUp' || event.key === 'PageDown') {
      event.preventDefault();
      const direction = event.key === 'PageUp' ? -1 : 1;
      moveCursor(addMonths(cursor, direction * (event.shiftKey ? 12 : 1)));
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      pick(cursor);
    }
  };

  const rangeEnd = mode === 'range' && to === '' && preview !== '' ? preview : to;
  const isSelected = (iso: string) =>
    mode === 'range' ? iso === from || iso === to : iso === value;
  const inRange = (iso: string) =>
    mode === 'range' && from !== '' && rangeEnd !== '' && isBetween(iso, from, rangeEnd);

  const cursorVisible =
    compareIso(startOfMonth(cursor), visibleMonth) >= 0 &&
    compareIso(startOfMonth(cursor), lastVisible) <= 0;
  const tabStop = cursorVisible ? cursor : visibleMonth;

  const banded = mode === 'range' && from !== '' && rangeEnd !== '' && from !== rangeEnd;
  const bandStart = banded ? (compareIso(from, rangeEnd) <= 0 ? from : rangeEnd) : '';
  const bandEnd = banded ? (compareIso(from, rangeEnd) <= 0 ? rangeEnd : from) : '';

  const prevDisabled = min != null && min !== '' && compareIso(addMonths(visibleMonth, -1), startOfMonth(min)) < 0;
  const nextDisabled =
    max != null && max !== '' && compareIso(addMonths(lastVisible, 1), startOfMonth(max)) > 0;

  return (
    <div
      className={cn(styles.calendar, className)}
      data-months={months}
      data-range={mode === 'range' ? '' : undefined}
      aria-label={props['aria-label']}
    >
      <div className={styles.head}>
        <IconButton
          size="sm"
          label="Предыдущий месяц"
          disabled={prevDisabled}
          onClick={() => setMonth(addMonths(visibleMonth, -1))}
        >
          <ChevronLeft size={16} aria-hidden />
        </IconButton>
        { }
        {months === 1 ? (
          <button
            type="button"
            className={styles.caption}
            aria-expanded={picking === 0}
            onClick={() => setPicking((current) => (current === 0 ? null : 0))}
          >
            {monthLabel(visibleMonth)}
          </button>
        ) : (
          <span />
        )}
        <IconButton
          size="sm"
          label="Следующий месяц"
          disabled={nextDisabled}
          onClick={() => setMonth(addMonths(visibleMonth, 1))}
        >
          <ChevronRight size={16} aria-hidden />
        </IconButton>
      </div>

      <div
          ref={gridRef}
          className={styles.grids}
          onKeyDown={onKeyDown}
          onMouseLeave={() => onPreviewChange?.('')}
        >
          {Array.from({ length: months }, (_, index) => {
            const gridMonth = addMonths(visibleMonth, index);
            return (
              <div key={gridMonth} className={styles.month}>
                {months > 1 && (
                  <button
                    type="button"
                    className={styles.monthName}
                    aria-expanded={picking === index}
                    onClick={() => setPicking((current) => (current === index ? null : index))}
                  >
                    {monthLabel(gridMonth)}
                  </button>
                )}
                {picking === index ? (
                  <MonthPicker
                    month={gridMonth}
                    onPick={(next) => {
                      setMonth(addMonths(next, -index));
                      setPicking(null);
                    }}
                  />
                ) : (
                  <>
                <div className={styles.weekdays} aria-hidden>
                  {WEEKDAYS.map((day) => (
                    <span key={day} className={styles.weekday}>
                      {day}
                    </span>
                  ))}
                </div>
                <div className={styles.grid} role="grid">
                  {monthGrid(gridMonth).map((week) => (
                    <div key={week[0]} className={styles.week} role="row">
                      {week.map((iso) => {
                        const outside = !isSameMonth(iso, gridMonth);
                        const selected = isSelected(iso);
                        const disabled = dayDisabled(iso);
                        return (
                          <button
                            key={iso}
                            type="button"
                            role="gridcell"
                            data-iso={iso}
                            className={styles.day}
                            tabIndex={iso === tabStop ? 0 : -1}
                            aria-label={formatRuLong(iso)}
                            aria-selected={selected}
                            aria-disabled={disabled || undefined}
                            aria-current={iso === today ? 'date' : undefined}
                            data-selected={selected || undefined}
                            data-in-range={inRange(iso) && !selected ? '' : undefined}
                            data-range-start={iso === bandStart ? '' : undefined}
                            data-range-end={iso === bandEnd ? '' : undefined}
                            data-today={iso === today ? '' : undefined}
                            data-outside={outside || undefined}
                            data-weekend={isWeekend(iso) || undefined}
                            data-disabled={disabled || undefined}
                            onClick={() => pick(iso)}
                            onFocus={() => setCursor(iso)}
                            onMouseEnter={() => onPreviewChange?.(iso)}
                          >
                            {Number(iso.slice(8, 10))}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
                  </>
                )}
              </div>
            );
          })}
      </div>

      {footer != null && <div className={styles.footer}>{footer}</div>}
    </div>
  );
};

const MonthPicker: React.FC<{ month: string; onPick: (month: string) => void }> = ({
  month,
  onPick,
}) => {
  const [year, setYear] = React.useState(() => Number(month.slice(0, 4)));
  const names = React.useMemo(() => monthNames(), []);
  const current = Number(month.slice(5, 7));
  const sameYear = year === Number(month.slice(0, 4));

  return (
    <div className={styles.picker}>
      <div className={styles.years}>
        <IconButton size="sm" label="Предыдущий год" onClick={() => setYear((y) => y - 1)}>
          <ChevronLeft size={16} aria-hidden />
        </IconButton>
        <span className={styles.year}>{year}</span>
        <IconButton size="sm" label="Следующий год" onClick={() => setYear((y) => y + 1)}>
          <ChevronRight size={16} aria-hidden />
        </IconButton>
      </div>
      <div className={styles.months}>
        {names.map((name, index) => {
          const selected = sameYear && index + 1 === current;
          return (
            <button
              key={name}
              type="button"
              className={styles.monthBtn}
              data-selected={selected || undefined}
              aria-pressed={selected}
              onClick={() => onPick(`${year}-${String(index + 1).padStart(2, '0')}-01`)}
            >
              {name}
            </button>
          );
        })}
      </div>
    </div>
  );
};
